import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ALL_PROFILES_QUERY_KEY } from "@/lib/users-cache";
import { getUnknownErrorMessage } from "@/lib/error-message";
import { userService } from "@/services/UserService";
import type { CreateUserFormValues } from "@/schemas/user";
import type { Profile } from "@/types/users";

type CreateUserMutationContext = {
  previousProfiles: Profile[] | undefined;
};

const buildOptimisticProfile = (values: CreateUserFormValues): Profile => ({
  id: `optimistic-${crypto.randomUUID()}`,
  email: values.email,
  first_name: values.firstName,
  last_name: values.lastName,
  role: values.role,
  team: values.team,
  manager_id: null,
  created_at: new Date().toISOString(),
});

export const useCreateUserMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateUserFormValues) =>
      userService.create({
        email: values.email,
        password: values.password,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role,
        team: values.team,
      }),
    onMutate: async (
      values,
    ): Promise<CreateUserMutationContext> => {
      await queryClient.cancelQueries({ queryKey: ALL_PROFILES_QUERY_KEY });

      const previousProfiles = queryClient.getQueryData<Profile[]>(
        ALL_PROFILES_QUERY_KEY,
      );

      queryClient.setQueryData<Profile[]>(ALL_PROFILES_QUERY_KEY, (old) => [
        buildOptimisticProfile(values),
        ...(old ?? []),
      ]);

      return { previousProfiles };
    },
    onSuccess: () => {
      toast.success("User created successfully!");
    },
    onError: (error: unknown, _values, context) => {
      if (context?.previousProfiles !== undefined) {
        queryClient.setQueryData(
          ALL_PROFILES_QUERY_KEY,
          context.previousProfiles,
        );
      }
      toast.error(getUnknownErrorMessage(error));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALL_PROFILES_QUERY_KEY });
    },
  });
};
